import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,Stack,
} from '@mui/material';
import { subscriptionService } from '../../services/subscriptionService';
import { adminService } from '../../services/adminService';

const SubscriptionModal = ({ open, onClose, onSuccess, subscription = null }) => {
  const [formData, setFormData] = useState({
    utilisateurId: '',
    formuleId: '',
    dateDebut: '',
    dateFin: '',
    statut: 'actif'
  });
  const [vendors, setVendors] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [error, setError] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscriptionDurationDays, setSubscriptionDurationDays] = useState(30); // Par défaut 30 jours

  useEffect(() => {
    if (open) {
      loadVendors();
      loadPlans();
      loadSubscriptionDurationDays();
      setError('');
    }
  }, [open]);

  const loadSubscriptionDurationDays = async () => {
    try {
      const response = await adminService.getSystemSetting('subscription_duration_days');
      if (response && response.value !== null && response.value !== undefined) {
        setSubscriptionDurationDays(parseInt(response.value) || 30);
      }
    } catch (err) {
      console.error('Erreur lors du chargement de la durée d\'abonnement:', err);
      // Garder la valeur par défaut de 30 jours
    }
  };

  useEffect(() => {
    if (subscription && vendors.length > 0 && plans.length > 0) {
      // Définir les objets sélectionnés pour les Autocomplete
      const vendor = vendors.find(v => v.id === subscription.utilisateurId);
      const plan = plans.find(p => p.id === subscription.formuleId);
      
      setFormData({
        utilisateurId: subscription.utilisateurId || '',
        formuleId: subscription.formuleId || '',
        dateDebut: subscription.dateDebut ? new Date(subscription.dateDebut).toISOString().split('T')[0] : '',
        dateFin: subscription.dateFin ? new Date(subscription.dateFin).toISOString().split('T')[0] : '',
        statut: subscription.statut || 'actif',
        dateTransaction: subscription.dateTransaction ? new Date(subscription.dateTransaction).toISOString().split('T')[0] : '',
        numeroTelephone: subscription.numeroTelephone || '',
        referenceTransaction: subscription.referenceTransaction || '',
        mode: subscription.mode || '',
        montant: subscription.montant || ''
      });
      
      setSelectedVendor(vendor || null);
      setSelectedPlan(plan || null);
    } else if (!subscription) {
      // Calculer la date de fin en utilisant le paramètre système
      const dateDebut = new Date();
      const dateFin = new Date(dateDebut);
      dateFin.setDate(dateFin.getDate() + subscriptionDurationDays);
      
      setFormData({
        utilisateurId: '',
        formuleId: '',
        dateDebut: dateDebut.toISOString().split('T')[0],
        dateFin: dateFin.toISOString().split('T')[0],
        statut: 'actif'
      });
      setSelectedVendor(null);
      setSelectedPlan(null);
    }
  }, [subscription, vendors, plans, subscriptionDurationDays]);

  const loadVendors = async () => {
    setLoadingVendors(true);
    try {
      const data = await adminService.getUsers({ role: 'VENDOR' });
      setVendors(data.users || []);
    } catch (err) {
      console.error('Erreur lors du chargement des vendeurs:', err);
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const data = await subscriptionService.getSubscriptionPlans();
      // console.log('Plans data received:', data);
      setPlans(data.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des formules:', err);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVendorChange = (event, newValue) => {
    setSelectedVendor(newValue);
    setFormData(prev => ({
      ...prev,
      utilisateurId: newValue ? newValue.id : ''
    }));
  };

  const handlePlanChange = (event, newValue) => {
    setSelectedPlan(newValue);
    setFormData(prev => ({
      ...prev,
      formuleId: newValue ? newValue.id : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (subscription) {
        // Modification d'un abonnement existant
        await subscriptionService.updateSubscription(subscription.id, formData);
        onSuccess('Abonnement modifié avec succès');
      } else {
        // Création d'un nouvel abonnement
        await subscriptionService.createSubscription(formData);
        onSuccess('Abonnement attribué avec succès');
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {subscription ? "Modifier l'abonnement" : "Attribuer un abonnement"}
      </DialogTitle>
  
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
  
          <Stack spacing={3}>
            {/* Sélection du vendeur */}
            <Autocomplete
              options={vendors}
              value={selectedVendor}
              onChange={handleVendorChange}
              getOptionLabel={(option) =>
                option ? `${option.name} (${option.email || option.phone})` : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingVendors}
              disabled={loadingVendors}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendeur"
                  required
                  placeholder="Rechercher un vendeur..."
                  helperText="Tapez pour rechercher un vendeur"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1" fontWeight="medium">
                    {option.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.email || option.phone}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                loadingVendors
                  ? "Chargement des vendeurs..."
                  : "Aucun vendeur trouvé"
              }
            />
  
            {/* Sélection de la formule */}
            <Autocomplete
              options={plans}
              value={selectedPlan}
              onChange={handlePlanChange}
              getOptionLabel={(option) =>
                option
                  ? `${option.libelle} - ${option.nombreProduit} produits`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              loading={loadingPlans}
              disabled={loadingPlans}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Formule d'abonnement"
                  required
                  placeholder="Rechercher une formule..."
                  helperText="Tapez pour rechercher une formule"
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1" fontWeight="medium">
                    {option.libelle}  
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                     |  {option.nombreProduit} produits • {option.prix} FCFA  | 
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                         {option.description}
                    </Typography>
                  )}
                </Box>
              )}
              noOptionsText={
                loadingPlans
                  ? "Chargement des formules..."
                  : "Aucune formule trouvée"
              }
            />
  
            {/* Dates */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
            >
              <TextField
                name="dateDebut"
                label="Date de début"
                type="date"
                value={formData.dateDebut}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                name="dateFin"
                label="Date de fin"
                type="date"
                value={formData.dateFin}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            {/* Informations de paiement - Affichage en lecture seule si l'abonnement existe */}
            {subscription && (
              <>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Informations de paiement
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider'
                }}>
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Date de transaction
                        </Typography>
                        <Typography variant="body1">
                          {formData.dateTransaction 
                            ? new Date(formData.dateTransaction).toLocaleDateString('fr-FR')
                            : 'Non renseigné'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Numéro de téléphone
                        </Typography>
                        <Typography variant="body1">
                          {formData.numeroTelephone || 'Non renseigné'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Référence de transaction
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {formData.referenceTransaction || 'Non renseigné'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Mode de paiement
                        </Typography>
                        <Typography variant="body1">
                          {formData.mode || 'Non renseigné'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                          Montant
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formData.montant 
                            ? `${parseFloat(formData.montant).toLocaleString('fr-FR')} FCFA`
                            : 'Non renseigné'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
  
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Sauvegarde..." : subscription ? "Modifier" : "Attribuer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
  
  
};

export default SubscriptionModal;
